import type { Dict } from '../i18n';

const dict: Dict = {
  en: {
    // Header
    meta_subtitle: 'Order pipeline · all actors',
    heading: 'Swimlane view',

    // Legend labels
    legend_agent_action: 'agent action',
    legend_automation: 'automation',
    legend_admin: 'admin',
    legend_client: 'client',

    // Swimlane lane labels
    lane_agent: 'Agent',
    lane_system: 'System',
    lane_admin: 'Admin',
    lane_client: 'Client',

    // Event labels (main text on each card)
    event_submit_order: 'Submit order',
    event_create_order: 'Create order',
    event_match_client: 'Match client in Holded',
    event_generate_proforma: 'Generate proforma',
    event_receive_proforma: 'Receive proforma',
    event_pay: 'Pay',
    event_auto_detect_payment: 'Auto-detect payment',
    event_confirm_match: 'Confirm match',
    event_generate_invoice: 'Generate invoice',
    event_receive_invoice: 'Receive invoice',
    event_generate_waybill: 'Generate waybill',
    event_create_sendcloud_label: 'Create Sendcloud label',
    event_tracking_notification: 'Tracking notification',
    event_poll_sendcloud: 'Poll Sendcloud',
    event_status_delivered: 'Status → delivered',

    // Event sub-labels (descriptive hint beneath each card label)
    sub_fills_form: 'fills form',
    sub_persist_assign_id: 'persist + assign id',
    sub_create_if_missing: 'create if missing',
    sub_pdf_payment_url: 'pdf · payment URL',
    sub_email_agent_cc: 'email · agent cc',
    sub_stripe_pp_iban: 'Stripe / PP / IBAN',
    sub_webhook_bank_sync: 'webhook or bank sync',
    sub_iban_only_phase1: 'IBAN only · Phase 1',
    sub_from_proforma: 'from proforma',
    sub_email: 'email',
    sub_pdf: 'pdf',
    sub_tracking_id: 'tracking id',
    sub_whatsapp_email: 'whatsapp + email',
    sub_every_few_hours: 'every few hours',
    sub_or_delivery_problem: 'or → delivery_problem',

    // Hold branch annotation
    hold_branch_label: '↳ Branch · on_hold_admin_review{{future}}',
    hold_branch_future: ' · later phase',
    hold_branch_body:
      'SEPA / 30d postpay route here BEFORE "paid". Admin approves → re-enters main flow.',
  },
  es: {
    // Header
    meta_subtitle: 'Flujo del pedido · todos los actores',
    heading: 'Vista de carriles',

    // Legend labels
    legend_agent_action: 'acción del agente',
    legend_automation: 'automatización',
    legend_admin: 'administrador',
    legend_client: 'cliente',

    // Swimlane lane labels
    lane_agent: 'Agente',
    lane_system: 'Sistema',
    lane_admin: 'Admin',
    lane_client: 'Cliente',

    // Event labels
    event_submit_order: 'Enviar pedido',
    event_create_order: 'Crear pedido',
    event_match_client: 'Vincular cliente en Holded',
    event_generate_proforma: 'Generar proforma',
    event_receive_proforma: 'Recibir proforma',
    event_pay: 'Pagar',
    event_auto_detect_payment: 'Detectar pago automáticamente',
    event_confirm_match: 'Confirmar coincidencia',
    event_generate_invoice: 'Generar factura',
    event_receive_invoice: 'Recibir factura',
    event_generate_waybill: 'Generar albarán',
    event_create_sendcloud_label: 'Crear etiqueta Sendcloud',
    event_tracking_notification: 'Notificación de seguimiento',
    event_poll_sendcloud: 'Consultar Sendcloud',
    event_status_delivered: 'Estado → entregado',

    // Event sub-labels
    sub_fills_form: 'rellena formulario',
    sub_persist_assign_id: 'persistir + asignar id',
    sub_create_if_missing: 'crear si no existe',
    sub_pdf_payment_url: 'pdf · URL de pago',
    sub_email_agent_cc: 'email · copia al agente',
    sub_stripe_pp_iban: 'Stripe / PP / IBAN',
    sub_webhook_bank_sync: 'webhook o sincronización bancaria',
    sub_iban_only_phase1: 'Solo IBAN · Fase 1',
    sub_from_proforma: 'desde la proforma',
    sub_email: 'email',
    sub_pdf: 'pdf',
    sub_tracking_id: 'id de seguimiento',
    sub_whatsapp_email: 'whatsapp + email',
    sub_every_few_hours: 'cada pocas horas',
    sub_or_delivery_problem: 'o → delivery_problem',

    // Hold branch annotation
    hold_branch_label: '↳ Rama · on_hold_admin_review{{future}}',
    hold_branch_future: ' · fase posterior',
    hold_branch_body:
      'Ruta SEPA / pago diferido 30d ANTES de "pagado". El administrador aprueba → vuelve al flujo principal.',
  },
  de: {
    // Header
    meta_subtitle: 'Bestellablauf · alle Beteiligten',
    heading: 'Swimlane-Ansicht',

    // Legend labels
    legend_agent_action: 'Agentenaktion',
    legend_automation: 'Automatisierung',
    legend_admin: 'Admin',
    legend_client: 'Kunde',

    // Swimlane lane labels
    lane_agent: 'Agent',
    lane_system: 'System',
    lane_admin: 'Admin',
    lane_client: 'Kunde',

    // Event labels
    event_submit_order: 'Bestellung absenden',
    event_create_order: 'Bestellung erstellen',
    event_match_client: 'Kunde in Holded zuordnen',
    event_generate_proforma: 'Proforma erstellen',
    event_receive_proforma: 'Proforma erhalten',
    event_pay: 'Bezahlen',
    event_auto_detect_payment: 'Zahlung automatisch erkennen',
    event_confirm_match: 'Zuordnung bestätigen',
    event_generate_invoice: 'Rechnung erstellen',
    event_receive_invoice: 'Rechnung erhalten',
    event_generate_waybill: 'Lieferschein erstellen',
    event_create_sendcloud_label: 'Sendcloud-Etikett erstellen',
    event_tracking_notification: 'Sendungsbenachrichtigung',
    event_poll_sendcloud: 'Sendcloud abfragen',
    event_status_delivered: 'Status → zugestellt',

    // Event sub-labels
    sub_fills_form: 'füllt Formular aus',
    sub_persist_assign_id: 'speichern + ID zuweisen',
    sub_create_if_missing: 'erstellen, falls nicht vorhanden',
    sub_pdf_payment_url: 'PDF · Zahlungs-URL',
    sub_email_agent_cc: 'E-Mail · Agent in Kopie',
    sub_stripe_pp_iban: 'Stripe / PP / IBAN',
    sub_webhook_bank_sync: 'Webhook oder Bankabgleich',
    sub_iban_only_phase1: 'Nur IBAN · Phase 1',
    sub_from_proforma: 'aus Proforma',
    sub_email: 'E-Mail',
    sub_pdf: 'PDF',
    sub_tracking_id: 'Sendungsnummer',
    sub_whatsapp_email: 'WhatsApp + E-Mail',
    sub_every_few_hours: 'alle paar Stunden',
    sub_or_delivery_problem: 'oder → delivery_problem',

    // Hold branch annotation
    hold_branch_label: '↳ Zweig · on_hold_admin_review{{future}}',
    hold_branch_future: ' · spätere Phase',
    hold_branch_body:
      'SEPA / 30-Tage-Zahlungsziel hier VOR "bezahlt". Admin genehmigt → kehrt in den Hauptablauf zurück.',
  },
};

export default dict;
